// src/components/ClassificationList.jsx
import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import Snippet from './Snippet'; // Make sure the path is correct
import { SpeakerWaveIcon, XMarkIcon, ArrowsUpDownIcon } from '@heroicons/react/24/solid'; // Import XMarkIcon for badges & ArrowsUpDownIcon

// --- Sorting Helpers (Keep these) ---
const valueOrder = { 'high': 1, 'medium': 2, 'low': 3 };

const compareSnippets = (a, b) => {
    // Compare by 'value' first (High > Medium > Low)
    const valueA = a.value?.trim().toLowerCase();
    const valueB = b.value?.trim().toLowerCase();
    const orderA = valueOrder[valueA] || 99; // Assign a high number if value is missing/unexpected
    const orderB = valueOrder[valueB] || 99;

    if (orderA !== orderB) {
        return orderA - orderB; // Sorts low values (higher order number) towards the end
    }

    // If values are the same or not comparable, sort by date (most recent first)
    try {
        // Check if dateaired exists and is a valid date string format
        const dateAStr = a.video?.dateaired;
        const dateBStr = b.video?.dateaired;

        // Attempt to create Date objects, handle potential invalid formats gracefully
        const dateA = dateAStr ? new Date(dateAStr) : null;
        const dateB = dateBStr ? new Date(dateBStr) : null;

        // Check if dates are valid Date objects
        const isValidA = dateA instanceof Date && !isNaN(dateA);
        const isValidB = dateB instanceof Date && !isNaN(dateB);

        if (isValidA && isValidB) {
            // Sort descending by time (most recent first)
            return dateB.getTime() - dateA.getTime();
        } else if (isValidA) {
            return -1; // Valid date A comes before invalid/missing date B
        } else if (isValidB) {
            return 1;  // Valid date B comes before invalid/missing date A
        } else {
            return 0; // Both dates are invalid or missing, treat as equal
        }
    } catch (e) {
        // Log error if date parsing fails unexpectedly
        console.error("Error parsing dates during sort:", a.video?.dateaired, b.video?.dateaired, e);
        return 0; // Treat as equal if error occurs
    }
};
// --- End Sorting Helpers ---


// --- Filter Badge Component ---
const FilterBadge = ({ word, onRemove }) => (
    <div className="flex items-center bg-info text-info-content rounded-full px-3 py-1 text-sm shadow">
        <span className="font-medium">{word}</span>
        <button
            onClick={() => onRemove(word)}
            className="ml-1.5 p-0.5 rounded-full hover:bg-black/20 focus:outline-none focus:ring-1 focus:ring-info-content"
            aria-label={`Remove filter: ${word}`}
        >
            {/* Using a smaller X mark icon */}
            <XMarkIcon className="w-3 h-3 stroke-current stroke-2" />
        </button>
    </div>
);


// --- Main ClassificationList Component ---
const ClassificationList = ({
    allSnippets, // Renamed: Represents snippets *after* date/value/source filters from PlayerPage
    selectedFilterWords, // New prop: array of selected words from WordCloud
    onRemoveFilterWord, // New prop: function to remove a word filter
    onAddFilterWord,
    onSnippetClick,
    onPause,
    onResume,
    onStop, // Make sure this is called by cancelPlayback
    isPlaying,
    currentSnippetId
}) => {
    // --- State ---
    const [groupBy, setGroupBy] = useState('classification'); // 'classification' or 'value'
    const [expandedGroup, setExpandedGroup] = useState(null); // Tracks which group is open
    const [playingGroup, setPlayingGroup] = useState(null); // Tracks which group is in "Play All" mode
    const [currentPlayAllIndex, setCurrentPlayAllIndex] = useState(0); // Index for "Play All"
    const [timeLeft, setTimeLeft] = useState(30); // Timer for "Play All"
    const [manualFilterInput, setManualFilterInput] = useState('');
    // --- Refs ---
    const snippetRefs = useRef({}); // For potentially scrolling to the active snippet
    const intervalRef = useRef(null); // For the "Play All" timer

    // --- Filter snippets based on selectedFilterWords ---
    // This is the core filtering logic based on the words selected in the WordCloud
    const wordFilteredSnippets = useMemo(() => {
        if (!selectedFilterWords || selectedFilterWords.length === 0) {
            return allSnippets || []; // Return the passed-in snippets if no word filters are active
        }
        // Prepare filter words (lowercase) for efficient checking
        const lowerCaseFilterWords = selectedFilterWords.map(word => word.toLowerCase());

        return (allSnippets || []).filter(s => {
            const snippetTextLower = s.snippet?.toLowerCase() || '';
            // "AND" logic: Check if the snippet text includes ALL filter words
            return lowerCaseFilterWords.every(filterWord => snippetTextLower.includes(filterWord));
        });
    }, [allSnippets, selectedFilterWords]); // Recalculate when the base snippets or the filter words change

    // --- Calculate unique classification names from the *word-filtered* list ---
    const classifications = useMemo(() => {
        if (groupBy !== 'classification') return []; // Only calculate if grouping by classification
        const validClassifications = wordFilteredSnippets // Use the word-filtered list here
            ?.map(s => s.classification)
            .filter(c => c != null && c !== '') || []; // Ensure not null or empty string

        return [...new Set(validClassifications)].sort(); // Get unique names and sort alphabetically
    }, [wordFilteredSnippets, groupBy]); // Depends on the result of the word filtering and groupBy

    // --- Define Value Groups (for groupBy 'value') ---
    const valueGroups = useMemo(() => {
        if (groupBy !== 'value') return [];
        // Predefined order
        return ['High', 'Medium', 'Low'].filter(valueLabel =>
            wordFilteredSnippets.some(s => s.value?.trim().toLowerCase() === valueLabel.toLowerCase())
        );
    }, [wordFilteredSnippets, groupBy]);

    // --- Effect: Cleanup Play All timer on unmount ---
    useEffect(() => {
        return () => {
            clearInterval(intervalRef.current); // Clear interval when component unmounts
        };
    }, []);

     // --- Effect: Stop playback if the current snippet disappears due to filtering ---
     useEffect(() => {
        if (currentSnippetId && wordFilteredSnippets.length > 0) {
            const isCurrentSnippetStillVisible = wordFilteredSnippets.some(s => s.id === currentSnippetId);
            if (!isCurrentSnippetStillVisible) {
                // If the currently playing/selected snippet is no longer in the filtered list
                if (playingGroup) {
                     // If it was part of a "Play All", cancel the sequence
                    cancelPlayback();
                } else {
                     // If it was individually selected, just stop it
                    onStop(); // Use the passed onStop handler
                }
            }
        }
        // Reset play all if the playing group disappears or becomes empty
        if (playingGroup) {
            let groupStillExists = false;
            if (groupBy === 'classification') {
                groupStillExists = classifications.includes(playingGroup);
            } else if (groupBy === 'value') {
                groupStillExists = valueGroups.includes(playingGroup);
            }

            if (!groupStillExists) {
                 cancelPlayback();
            } else {
                const snippetsInPlayingGroup = getSnippetsForGroup(playingGroup);
                if (snippetsInPlayingGroup.length === 0) {
                    cancelPlayback();
                }
            }
        }

    }, [wordFilteredSnippets, currentSnippetId, playingGroup, classifications, valueGroups, groupBy]); // Re-check when filtered list or playing state changes


    // --- Handlers ---
    const toggleGroup = (groupName) => {
        setExpandedGroup(prev => (prev === groupName ? null : groupName));
        // Collapse others when one is expanded? Optional UX choice.
        // If expanding, maybe stop any existing 'Play All' from another group
        if (expandedGroup !== groupName && playingGroup && playingGroup !== groupName) {
             cancelPlayback();
        }
    };

    // Memoized getter for snippets within a specific group (classification or value)
    const getSnippetsForGroup = useCallback((groupName) => {
        let filteredByGroup;
        if (groupBy === 'classification') {
            filteredByGroup = wordFilteredSnippets?.filter(s => s.classification === groupName);
        } else if (groupBy === 'value') {
            filteredByGroup = wordFilteredSnippets?.filter(s => s.value?.trim().toLowerCase() === groupName.toLowerCase());
        } else {
            return [];
        }
        // Sort them according to the defined logic (value first, then date) for classifications
        // For value groups, primary sort is already by value (the group itself), so just sort by date
        return filteredByGroup?.sort(compareSnippets) || [];
    }, [wordFilteredSnippets, groupBy, compareSnippets]);

    // --- "Play All" Logic & Handlers (using getSnippetsForGroup) ---
    const startTimer = (snippetsForTimer, index) => {
        clearInterval(intervalRef.current); // Clear any existing timer
        setTimeLeft(30); // Reset timer display

        intervalRef.current = setInterval(() => {
            setTimeLeft(prev => {
                if (prev <= 1) { // Timer reached zero
                    clearInterval(intervalRef.current);
                    const nextIndex = index + 1;
                    if (nextIndex < snippetsForTimer.length) { // More snippets in the list
                        const nextSnippet = snippetsForTimer[nextIndex];
                        setCurrentPlayAllIndex(nextIndex);
                        onSnippetClick(nextSnippet); // Trigger playback of the next snippet
                        setTimeLeft(30); // Reset timer for the new snippet
                        startTimer(snippetsForTimer, nextIndex); // Start timer for the new snippet
                    } else {
                        // Reached the end of the list
                        cancelPlayback(); // Stop the "Play All" sequence
                    }
                    return 30; // Return value for setTimeLeft (doesn't matter here)
                }
                // Decrement timer if not yet zero
                return prev - 1;
            });
        }, 1000); // Update every second
    };

    const playAll = (groupName) => {
        const sortedList = getSnippetsForGroup(groupName); // Get snippets for this group
        if (sortedList.length === 0) return; // Do nothing if no snippets

        cancelPlayback(); // Stop any previous playback first

        setPlayingGroup(groupName); // Mark this group as playing
        setExpandedGroup(groupName); // Ensure it's expanded
        setCurrentPlayAllIndex(0); // Start from the first snippet
        setTimeLeft(30); // Reset timer display
        onSnippetClick(sortedList[0]); // Trigger playback of the first snippet
        startTimer(sortedList, 0); // Start the timer sequence

        // Optional: Scroll the first snippet into view
         if (snippetRefs.current[sortedList[0].id]) {
             snippetRefs.current[sortedList[0].id].scrollIntoView({ behavior: 'smooth', block: 'nearest' });
         }
    };

    const pausePlayback = () => {
        clearInterval(intervalRef.current); // Stop the timer
        onPause(); // Call the pause handler passed from PlayerPage
    };

    const resumePlayback = () => {
        // Check if we are in a "Play All" state and have snippets
        const sortedList = getSnippetsForGroup(playingGroup);
        if (playingGroup && sortedList.length > 0 && currentPlayAllIndex < sortedList.length) {
            // Ensure the currently indexed snippet is still the one selected externally
            if (currentSnippetId === sortedList[currentPlayAllIndex]?.id) {
                 startTimer(sortedList, currentPlayAllIndex); // Restart timer for the current snippet
                 onResume(); // Call the resume handler passed from PlayerPage
            } else {
                // If external selection changed during pause, cancel Play All for safety
                console.warn("Playback state mismatch on resume. Cancelling Play All.");
                cancelPlayback();
            }
        } else if (!playingGroup && currentSnippetId) {
             // Handle resuming a single, individually selected snippet (if PlayerPage logic supports it)
             onResume();
        }
    };

    const skipToNext = () => {
        if (!playingGroup) return; // Only works during "Play All"

        clearInterval(intervalRef.current); // Stop current timer
        const sortedList = getSnippetsForGroup(playingGroup);
        const nextIndex = currentPlayAllIndex + 1;

        if (nextIndex < sortedList.length) { // Check if there is a next snippet
            setCurrentPlayAllIndex(nextIndex);
            const nextSnippet = sortedList[nextIndex];
            onSnippetClick(nextSnippet); // Trigger playback of the next snippet
            setTimeLeft(30); // Reset timer display
            startTimer(sortedList, nextIndex); // Start timer for the new snippet
        } else {
            // Skipped the last one, end playback
            cancelPlayback();
        }
    };

    const cancelPlayback = () => {
        clearInterval(intervalRef.current); // Stop the timer
        setPlayingGroup(null); // Clear the playing group state
        setCurrentPlayAllIndex(0); // Reset index
        setTimeLeft(30); // Reset timer display
        onStop(); // Call the stop handler passed from PlayerPage
    };

    // Wrapper for individual snippet clicks to potentially stop "Play All"
    const handleIndividualSnippetClick = (snippet) => {
        if (playingGroup) {
            cancelPlayback(); // Stop "Play All" if user clicks a specific snippet
        }
        onSnippetClick(snippet); // Call the main handler to play the clicked snippet
    };

    // Helper to check if a specific snippet is the currently active one
    const isSnippetPlaying = (snippet) => {
        return snippet?.id === currentSnippetId;
    };
    const handleManualInputChange = (event) => {
      setManualFilterInput(event.target.value);
    };

    const handleManualAddFilter = () => {
        const wordToAdd = manualFilterInput.trim(); // Trim whitespace
        if (wordToAdd.length > 0) {
            // Call the handler passed from PlayerPage
            // This handler now handles lowercasing and duplication checks
            onAddFilterWord?.(wordToAdd);
            setManualFilterInput(''); // Clear the input field
        }
    };

    const handleInputKeyDown = (event) => {
        // Check if the Enter key was pressed
        if (event.key === 'Enter') {
            event.preventDefault(); // Prevent default form submission behavior if applicable
            handleManualAddFilter();
        }
    };
    // --- End Handlers ---


    return (
        <div className="space-y-4">
            {/* Header */}
            <div className="flex justify-between items-center mb-2 text-accent">
                <h2 className="text-2xl font-semibold">
                    {groupBy === 'classification' ? 'Classifications' : 'Value'}
                </h2>
                <button
                    onClick={() => setGroupBy(prev => prev === 'classification' ? 'value' : 'classification')}
                    className="btn btn-ghost btn-sm p-1 hover:bg-base-300"
                    title={groupBy === 'classification' ? "Group by Value" : "Group by Classification"}
                >
                    <ArrowsUpDownIcon className="w-5 h-5 mr-1" />
                    Switch to {groupBy === 'classification' ? 'Value' : 'Classification'}
                </button>
            </div>
            <div className="mb-4 px-1">
                <label htmlFor="manual-filter-input" className="block text-xs font-medium text-base-content/70 mb-1">
                    Add Keyword Filter:
                </label>
                <div className="flex gap-2 items-center">
                     <input
                         type="text"
                         id="manual-filter-input"
                         value={manualFilterInput}
                         onChange={handleManualInputChange}
                         onKeyDown={handleInputKeyDown} // Add filter on Enter key
                         placeholder="Type keyword & press Enter..."
                         className="input input-bordered input-sm w-full max-w-xs flex-grow bg-base-100" // DaisyUI input classes
                     />
                     {/* Optional: Add button */}
                     {/* <button
                        className="btn btn-primary btn-sm flex-shrink-0"
                        onClick={handleManualAddFilter}
                        disabled={manualFilterInput.trim().length === 0} // Disable if input is empty
                     >
                         Add
                     </button> */}
                </div>
            </div>
            {/* --- Filter Badges Area --- */}
            {selectedFilterWords && selectedFilterWords.length > 0 && (
                <div className="mb-4 px-2 py-2 bg-base-100/80 rounded-md shadow-inner">
                     <p className="text-xs font-semibold text-base-content/70 mb-2 ml-1">Active Keyword Filters:</p>
                    <div className="flex flex-wrap gap-2">
                        {selectedFilterWords.map(word => (
                            <FilterBadge key={word} word={word} onRemove={onRemoveFilterWord} />
                        ))}
                    </div>
                </div>
            )}
            {/* --- End Filter Badges Area --- */}


            {/* Messages for no snippets based on MULTIPLE filtering stages */}
            {!allSnippets || allSnippets.length === 0 ? (
                 // Message if there were no snippets *before* word filtering (i.e., from PlayerPage filters)
                <p className="text-center text-base-content/70 italic px-4 py-2">No snippets match the current Time/Source/Value filters.</p>
            ) : wordFilteredSnippets.length === 0 ? (
                 // Message if PlayerPage filters had results, but word filters removed everything
                <p className="text-center text-base-content/70 italic px-4 py-2">No snippets match the selected keyword filters.</p>
            ): (groupBy === 'classification' && classifications.length === 0) ? (
                 // Message if word filters matched snippets, but none of the matches are classified
                 <p className="text-center text-base-content/70 italic px-4 py-2">Matching snippets found, but none are classified.</p>
            ) : (groupBy === 'value' && valueGroups.length === 0) ? (
                 <p className="text-center text-base-content/70 italic px-4 py-2">Matching snippets found, but none match High/Medium/Low value.</p>
            ) : (
                // --- Render Group Sections ---
                <ul className="space-y-3">
                    {(groupBy === 'classification' ? classifications : valueGroups).map((groupName) => {
                        // Get the already sorted snippets for this specific group
                        const sortedSnippetsForGroup = getSnippetsForGroup(groupName);
                        const isExpanded = expandedGroup === groupName;
                        const isCurrentGroupPlaying = playingGroup === groupName;
                        const displayCount = sortedSnippetsForGroup.length;

                        // This check is redundant now because groups are derived from non-empty lists,
                        // but kept just in case for safety. Should ideally never be 0 here.
                         if (displayCount === 0) { return null; }

                        return (
                            <li key={groupName} className="mb-3 bg-base-100 shadow rounded-lg p-3">
                                {/* Group Header (Toggle Button, Name, Count, Playback Controls) */}
                                <div className="flex justify-between items-center mb-2">
                                    {/* Left Side: Toggle and Name/Count */}
                                    <button
                                        className="flex items-center gap-2 text-left group focus:outline-none"
                                        onClick={() => toggleGroup(groupName)}
                                        aria-expanded={isExpanded}
                                    >
                                        <span className={`inline-block transform transition-transform duration-300 text-accent group-hover:text-accent-focus ${isExpanded ? 'rotate-90' : ''}`}>
                                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4"><path fillRule="evenodd" d="M7.21 14.77a.75.75 0 01.02-1.06L11.168 10 7.23 6.29a.75.75 0 111.04-1.08l4.5 4.25a.75.75 0 010 1.08l-4.5 4.25a.75.75 0 01-1.06-.02z" clipRule="evenodd" /></svg>
                                        </span>
                                        <span className="font-semibold text-accent group-hover:text-accent-focus">
                                            {groupBy === 'value' ? `${groupName} Value` : groupName} ({displayCount})
                                        </span>
                                    </button>

                                    {/* Right Side: Playback Controls */}
                                    <div className="flex-shrink-0">
                                        {!isCurrentGroupPlaying && (
                                            <button
                                                className="btn btn-primary btn-xs text-white" // Using xs for compactness
                                                onClick={() => playAll(groupName)}
                                                disabled={displayCount === 0} // Should already be non-zero here
                                                title={`Play all ${groupName} snippets`}
                                             >
                                                 Play All
                                             </button>
                                         )}
                                        {isCurrentGroupPlaying && (
                                            <div className="flex gap-1">
                                                {isPlaying ? (
                                                    <button className="btn btn-warning btn-xs text-white" onClick={pausePlayback}>Pause</button>
                                                ) : (
                                                    <button className="btn btn-success btn-xs text-white" onClick={resumePlayback}>Resume</button>
                                                )}
                                                <button
                                                     className="btn btn-info btn-xs text-white"
                                                     onClick={skipToNext}
                                                     disabled={currentPlayAllIndex >= displayCount - 1}
                                                     title="Skip to next snippet"
                                                 >
                                                     Skip
                                                 </button>
                                                 <button
                                                     className="btn btn-error btn-xs text-white"
                                                     onClick={cancelPlayback}
                                                     title="Stop Play All"
                                                 >
                                                     Cancel
                                                 </button>
                                             </div>
                                         )}
                                    </div>
                                </div>

                                {/* Snippet List Area (Accordion Content) */}
                                {/* Using CSS for transition instead of inline style for cleaner separation */}
                                <div
                                    className={`transition-all ease-in-out duration-300 grid ${
                                        isExpanded ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'
                                    }`}
                                >
                                     {/* Inner div needed for overflow and grid transition */}
                                    <div className={`overflow-hidden`}>
                                         {/* Add slight padding and potential scroll */}
                                        <div className={`pt-2 space-y-1 ${displayCount > 9 ? 'max-h-[400px] overflow-y-auto pr-2' : ''}`}>
                                            {/* Map over the sorted snippets for this group */}
                                            {sortedSnippetsForGroup.map((snippet) => {
                                                const isActive = isSnippetPlaying(snippet);
                                                return (
                                                    <div
                                                        key={snippet.id}
                                                        ref={el => (snippetRefs.current[snippet.id] = el)} // Assign ref for scrolling
                                                        className={`flex items-center gap-2 cursor-pointer p-1 rounded-md transition-colors duration-200 ${
                                                            isActive ? 'bg-primary/20 font-semibold' : 'hover:bg-base-200/50'
                                                        }`}
                                                        onClick={() => handleIndividualSnippetClick(snippet)} // Use wrapper handler
                                                        title={snippet.snippet} // Tooltip with full snippet text
                                                    >
                                                        {/* Indicator Icon */}
                                                        <div className="w-4 h-4 flex-shrink-0 flex items-center justify-center">
                                                            {isActive && isPlaying && (<SpeakerWaveIcon className="w-4 h-4 text-primary animate-pulse" />)}
                                                             {/* Optional: Show paused state? */}
                                                            {/* {isActive && !isPlaying && (<PauseIcon className="w-4 h-4 text-primary/70" />)} */}
                                                         </div>
                                                        {/* Snippet Component */}
                                                        <div className="flex-grow min-w-0">
                                                            <Snippet snippet={snippet} onClick={handleIndividualSnippetClick} />
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                         {/* Timer display - only show if this group is playing */}
                                         {isCurrentGroupPlaying && (
                                             <div className="text-center pt-2 pb-1 text-xs text-base-content text-opacity-70">
                                                 {isPlaying ? `Next in ${timeLeft}s` : "Paused"}
                                             </div>
                                         )}
                                    </div>
                                </div>
                            </li>
                        );
                    })}
                </ul>
            )}
        </div>
    );
};

export default ClassificationList;